import { Link } from '@mui/material';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useBannerQuery } from '../api/getBanner';
import { useWelcomeQuery } from '../api/getWelcomeDetails';
import PhoneNumberLink from '../components/styledComponents/PhoneNumberLink';
import { useSnackbarsContext } from '../store/SnackbarProvider';

const routesWithCATBanner = ['/', '/bills', '/policies', '/claims'];

const textContainsLink = (bannerText: string) => {
  return bannerText.includes('</a>');
};

const parseAnchorTag = (bannerText: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(bannerText, 'text/html');
  const anchorTag = doc.querySelector('a');

  if (anchorTag) {
    const href = anchorTag.getAttribute('href');
    const text = anchorTag.textContent;

    if (href && text) {
      return { href, text };
    }
  }
  return null;
};

const getBannerTextWithLink = (bannerText: string) => {
  const regex = /<a href="[^"]+">[^<]+<\/a>/;
  const textParts = bannerText.split(regex);
  const anchorTag = parseAnchorTag(bannerText);

  if (anchorTag && textParts.length > 1) {
    return (
      <>
        {textParts[0]}
        <Link href={anchorTag.href} underline="none" target="_blank">
          {anchorTag.text}
        </Link>
        {textParts[1]}
      </>
    );
  }
  return bannerText;
};

const disableChat = import.meta.env.VITE_CHAT_DISABLED === 'true';

const useGetCATBanner = (disable: boolean) => {
  const { data: welcomeData } = useWelcomeQuery();

  const riskStateValue =
    welcomeData &&
    welcomeData.policies.map((policy) => {
      return policy.riskState;
    });

  const riskStateList = Array.from(new Set(riskStateValue)).toString();

  const { enqueueSnackbar, closeSnackbar } = useSnackbarsContext();

  const { pathname } = useLocation();
  const shouldShowCATBanners = routesWithCATBanner.includes(pathname);

  const { data: bannerData, isLoading: bannerDataLoading } = useBannerQuery(
    riskStateList,
    disable || !shouldShowCATBanners
  );

  const bannerVariantMap = {
    alert: 'warning',
    info: 'info',
  };

  useEffect(() => {
    if (
      shouldShowCATBanners &&
      !bannerDataLoading &&
      bannerData &&
      bannerData?.length > 0
    ) {
      bannerData.forEach((banner, idx) => {
        enqueueSnackbar({
          ...(textContainsLink(banner.text)
            ? { content: getBannerTextWithLink(banner.text) }
            : { message: banner.text }),
          variant: bannerVariantMap[banner.type] as
            | 'warning'
            | 'info'
            | 'error'
            | 'success',
          key: `cat${idx}`,
          dismissible: false,
        });
      });
    }
  }, [bannerDataLoading, bannerData, shouldShowCATBanners]);

  useEffect(() => {
    if (welcomeData && shouldShowCATBanners && disableChat) {
      enqueueSnackbar({
        title: 'Chat is down for upgrades.',
        content: (
          <>
            Service agents can help you 24/7 at{' '}
            <PhoneNumberLink
              sx={{ fontSize: 'inherit' }}
              phoneNumber="888.980.5650"
            />
            .
          </>
        ),
        variant: 'warning',
        key: `chat-disabled-cat`,
        dismissible: false,
      });
    }
  }, [shouldShowCATBanners, welcomeData]);

  useEffect(() => {
    if (!routesWithCATBanner.includes(pathname)) {
      bannerData?.forEach((_, idx) => {
        closeSnackbar(`cat${idx}`);
      });

      if (disableChat) {
        closeSnackbar(`chat-disabled-cat`);
      }
    }
  }, [pathname]);
};

export default useGetCATBanner;
